package com.eladfinish.bible.contest.screens.main

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.Stable
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.di.config.AppInfo
import com.eladfinish.bible.contest.features.teruma.AboutTerumaUiModel
import com.eladfinish.bible.contest.screens.main.data.ButtonData
import com.eladfinish.bible.contest.screens.main.data.ButtonType
import com.eladfinish.bible.contest.screens.main.data.ItemMode
import com.eladfinish.bible.contest.ui.components.bottomSheet.FeatureSheetDismissMethod
import com.eladfinish.bible.contest.ui.menus.actions.MainToolbarAction
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList

@Immutable
data class MainScreenState(
    val buttonDataList: ImmutableList<Pair<ButtonType, ButtonData>>,
    val itemMode: ItemMode,
    val isDebugMode: Boolean,
    val hebrewGender: HebrewGender,
    val hebrewGenderDialogAlreadyShown: Boolean,
    val announcement: String,
    val aboutTerumaUiModel: AboutTerumaUiModel?,
    val shouldShowAds: Boolean,
    /** Whether to offer the "remove ads" purchase + "restore purchase" — independent of [shouldShowAds]. */
    val canPurchaseAdFree: Boolean,
    val dialogState: DialogState,
    val isHelpInToolbar: Boolean = false,
    /** The "what's new" sheet of חידונים אמיתיים is due, once the home screen is idle. */
    val isPastQuizzesSheetOffered: Boolean = false,
    val isPastQuizzesSheetVisible: Boolean = false,
    /** The "what's new" sheet of ווידג'ט פסוק מתחלף is due, once the home screen is idle. */
    val isVerseWidgetSheetOffered: Boolean = false,
    val isVerseWidgetSheetVisible: Boolean = false,
) : StateKt {

    @Stable
    sealed class DialogState {
        data class About(
            val appInfo: AppInfo,
            val aboutTerumaUiModel: AboutTerumaUiModel?,
        ) : DialogState()

        data class DebugStuff(
            val title: StringDesc,
            val content: String,
        ) : DialogState()

        data class Help(val content: String) : DialogState()
        data object ConfirmSupportAd : DialogState()
        data object ConfirmRewardedAd : DialogState()
        data object RewardEarnedUpsell : DialogState()
        data object None : DialogState()
    }
}

sealed class MainScreenAction : ActionKt {
    data class ToolbarClicked(val toolbarAction: MainToolbarAction) : MainScreenAction()

    /** Any home-screen button; [position] is its index in the grid as the user arranged it. */
    data class ButtonClicked(val buttonType: ButtonType, val position: Int) : MainScreenAction()

    data object ResetOrder : MainScreenAction()
    data object SwitchToDragMode : MainScreenAction()
    data object CancelDragMode : MainScreenAction()
    data class DoneDragMode(
        val newOrder: (List<Pair<ButtonType, ButtonData>>),
    ) : MainScreenAction()

    data class UpdateHebrewGender(val hebrewGender: HebrewGender) : MainScreenAction()
    data object ReadHebrewGender : MainScreenAction()
    data object AnnouncementClicked : MainScreenAction()
    data class UpdateAnnouncement(val text: String) : MainScreenAction()
    data class UpdateShowAds(val showAds: Boolean) : MainScreenAction()
    data object RefreshButtons : MainScreenAction()
    data class ShowAboutTeruma(val aboutTerumaUiModel: AboutTerumaUiModel) : MainScreenAction()
    data object AboutClicked : MainScreenAction()
    data object AboutLongClicked : MainScreenAction()
    data object MikudLongClicked : MainScreenAction()
    data object HelpClicked : MainScreenAction()
    data object ConfirmedSupportAd : MainScreenAction()
    data object ConfirmedRewardedAd : MainScreenAction()
    data class RemoveAdsClicked(val source: RemoveAdsSource) : MainScreenAction()

    /** The purchase button is on screen (scrolled into view), reported once per composition of it. */
    data object RemoveAdsButtonShown : MainScreenAction()
    data object RestorePurchaseClicked : MainScreenAction()
    data object ConsumeTestPurchaseClicked : MainScreenAction()
    data object ShowRewardEarnedUpsell : MainScreenAction()
    data object HideDialog : MainScreenAction()

    data object OfferPastQuizzesSheet : MainScreenAction()
    data object ShowPastQuizzesSheet : MainScreenAction()
    data class PastQuizzesSheetDismissed(val method: FeatureSheetDismissMethod) : MainScreenAction()
    data object PastQuizzesSheetCtaClicked : MainScreenAction()

    data object OfferVerseWidgetSheet : MainScreenAction()
    data object ShowVerseWidgetSheet : MainScreenAction()
    data class VerseWidgetSheetDismissed(val method: FeatureSheetDismissMethod) : MainScreenAction()
    data object VerseWidgetSheetCtaClicked : MainScreenAction()
}

sealed interface MainScreenEffect : EffectKt {
    data object CheckUpdate : MainScreenEffect
    data object RateApp : MainScreenEffect
    data object Upgrade : MainScreenEffect
    data object ShowSupportAd : MainScreenEffect
    data object ShowRewardedAd : MainScreenEffect
    data object StartRemoveAdsPurchase : MainScreenEffect
    data object RestorePurchase : MainScreenEffect
    data object ConsumeTestPurchase : MainScreenEffect
    data class ShowToast(val message: StringDesc) : MainScreenEffect
}

/** Where the "remove ads" offer was tapped. */
enum class RemoveAdsSource { MENU_BUTTON, REWARD_UPSELL }
